import { pageMetadata, syncMetadata } from "./lib/seo";
import { CATEGORY_LABELS, getAuthorRecord } from "./data/stories";
import GoogleAnalytics from "./components/GoogleAnalytics";
import CookiePreferences from "./components/CookiePreferences";
import TermsPage from "./components/TermsPage";
import AdSlot from "./components/AdSlot";
import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import TopBar from "./components/TopBar";
import Masthead from "./components/Masthead";
import Ticker from "./components/Ticker";
import HeroSection from "./components/HeroSection";
import GambianNews from "./components/GambianNews";
import CategorySection from "./components/CategorySection";
import SponsoredStrip from "./components/SponsoredStrip";
import VideosStrip from "./components/VideosStrip";
import NewsletterBand from "./components/NewsletterBand";
import EditorsPicks from "./components/EditorsPicks";
import Footer from "./components/Footer";
import ArticlePage from "./components/ArticlePage";
import CategoryPage from "./components/CategoryPage";
import AuthorPage from "./components/AuthorPage";


import AuthModal from "./components/AuthModal";
import SearchResultsPage from "./components/SearchResultsPage";
import BackToTop from "./components/BackToTop";
import { hydrateStories, hydrateFrontpage, refreshWordPress, isEditionFresh, getEffectiveSections, STORIES } from "./data/stories";
import { AuthProvider, useAuth } from "./context/AuthContext";
import {
  PrivacyPolicyPage,
  PrivacyChoicesPage,
  ContactUsPage,
  CorrectionsPage,
  AboutUsPage,
  AdvertisePage,
} from "./components/LegalPages";
import useLenis from "./hooks/useLenis";
import "./App.css";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  render() {
    if (this.state.error) {
      return (
        <div className="p-10 text-center">
          <h1 className="font-display text-2xl">Something went wrong.</h1>
          <p className="mt-2 text-neutral-500">Please reload the page.</p>
        </div>
      );
    }
    return this.props.children;
  }
}

export const openSearch = () =>
  window.dispatchEvent(new CustomEvent("std-open-search"));

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    const lenis = window.__lenis;
    if (lenis) lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const Home = () => (
  <div data-testid="homepage">
    <TopBar />
    <Masthead />
    <Ticker />
    <AdSlot placement="masthead-banner" />
    <main>
      <HeroSection />

      {/* Full-width category sections under the six cards (CMS-picked leads) */}
      {getEffectiveSections().map((section) => (
        <CategorySection key={section.id} section={section} />
      ))}

      {/* Sponsored placements on the shaded band */}
      <SponsoredStrip />

      <GambianNews />
      <VideosStrip />
      <NewsletterBand />
      <EditorsPicks />
    </main>
    <Footer />
  </div>
);

const Pages = () => {
  useLenis();
  const location = useLocation();
  const [storiesRev, setStoriesRev] = useState(0);
  useEffect(() => {
    const pathname=location.pathname;
    const slug=pathname.replace(/^\/|\/$/g,'').replace(/^story\//,'');
    const story=STORIES.find(s=>s.id===slug);
    const author=pathname.startsWith('/author/') ? getAuthorRecord(pathname.split('/')[2]) : null;
    const known=pathname==='/'||['/news','/search','/about-us','/contact-us','/corrections-feedback','/privacy-policy','/terms-of-use','/your-privacy-choices','/advertise-with-us'].includes(pathname.replace(/\/$/,''))||pathname.startsWith('/category/')||pathname.startsWith('/author/');
    const meta=pageMetadata({pathname,story,author,status:!known&&!story?404:200,category:CATEGORY_LABELS[pathname.split('/')[2]]});
    syncMetadata(meta);
  },[location.pathname,storiesRev]);
  const [ready,setReady] = useState(() => STORIES.length > 0);
  const [loadError,setLoadError] = useState(false);

  /* Reuse fresh server data on arrival; refresh stale editions on focus. */
  useEffect(() => {
    const refresh = () =>
      Promise.all([hydrateStories(), hydrateFrontpage()]).then(() =>
        {setStoriesRev((r) => r + 1);setReady(true);
         if (!isEditionFresh()) refreshWordPress().then(updated=>{if(updated)setStoriesRev(r=>r+1);});}
      ).catch(()=>setLoadError(true));
    refresh();
    window.addEventListener("focus", refresh);
    return () => window.removeEventListener("focus", refresh);
  }, []);

  if (!ready) return <div className="min-h-screen flex items-center justify-center font-display text-2xl">{loadError ? "Stories could not load. Please refresh." : "Loading The Standard…"}</div>;

  /* Google sign-in returns with #session_id=... — exchange it first, silently.
     Hooks all run above; only the render branches here. */
  if (location.hash && location.hash.includes("session_id=")) {
    return <Home />;
  }

  return (
    <>
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/news" element={<CategoryPage />} />
        <Route path="/search" element={<SearchResultsPage />} />
        <Route path="/story/:id" element={<ArticlePage />} />
        <Route path="/category/:id" element={<CategoryPage />} />
        <Route path="/author/:slug" element={<AuthorPage />} />

        <Route path="/terms-of-use" element={<TermsPage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
        <Route path="/your-privacy-choices" element={<PrivacyChoicesPage />} />
        <Route path="/contact-us" element={<ContactUsPage />} />
        <Route path="/corrections-feedback" element={<CorrectionsPage />} />
        <Route path="/about-us" element={<AboutUsPage />} />
        <Route path="/advertise-with-us" element={<AdvertisePage />} />
        <Route path="/:id" element={<ArticlePage />} />
        <Route path="*" element={<ArticlePage />} />
      </Routes>

      <BackToTop />
    </>
  );
};

export function AppContent() {
  return <ErrorBoundary><AuthProvider><GoogleAnalytics /><Pages /><AuthModal /><CookiePreferences /></AuthProvider></ErrorBoundary>;
}
function App() { return <BrowserRouter><AppContent /></BrowserRouter>; }
export default App;
